import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTimeClock } from '../hooks/useTimeClock';
import { TimecardDetailData } from '../../shared/types.js';
import Button from './Button';
import TimecardDetailReport from './TimecardDetailReport';
import { generateTimecardDetailData, getDateRangeForPreset, correctDateForTimezone, exportToCsv } from '../utils';
import ReportCriteriaModal, { ReportCriteria } from './ReportCriteriaModal';
import { PrintIcon, DownloadIcon } from '../components/icons';

const DetailReportGenerator: React.FC<{ initialFilterEmployeeId?: string; isEmployeeView?: boolean }> = ({ initialFilterEmployeeId, isEmployeeView }) => {
    const { employees, timeLogs, activities, overtimeRules, timeSettings, leaveEntries, leaveCategories, payrollSettings, displayGroups } = useTimeClock();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reportsData, setReportsData] = useState<TimecardDetailData[]>([]);
    const [currentCriteria, setCurrentCriteria] = useState<ReportCriteria | null>(null);
    const employeesInAnyGroup = useMemo(() => {
        const groupEmployeeIds = new Set(displayGroups.flatMap(g => g.employeeIds));
        return employees.filter(e => groupEmployeeIds.has(e.id));
    }, [employees, displayGroups]);
    const handleGenerateReport = useCallback((criteria: ReportCriteria) => {
        const { employeeIds, startDate, endDate } = criteria;
        const start = correctDateForTimezone(startDate);
        start.setHours(0, 0, 0, 0);
        const end = correctDateForTimezone(endDate);
        end.setHours(23, 59, 59, 999);
        const selectedEmployees = employees
            .filter(emp => employeeIds.includes(emp.id))
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
        const allGeneratedReports = selectedEmployees.map(employee => {
            return generateTimecardDetailData(employee, timeLogs, leaveEntries, leaveCategories, activities, start, end, overtimeRules, timeSettings);
        });
        const reportsToShow = !isEmployeeView
            ? allGeneratedReports.filter(report => {
                let unpaid = 0;
                Object.values(report.activitySummary).forEach(summary => { unpaid += summary.unpaid; });
                Object.values(report.leaveSummary).forEach(summary => { unpaid += summary.unpaid; });
                return report.grandTotals.paid > 0 || unpaid > 0;
            })
            : allGeneratedReports;
        setReportsData(reportsToShow);
        setCurrentCriteria(criteria);
        setIsModalOpen(false);
    }, [employees, timeLogs, activities, leaveEntries, leaveCategories, overtimeRules, timeSettings, isEmployeeView]);
    useEffect(() => {
        if (initialFilterEmployeeId && !currentCriteria) {
             const { start, end } = getDateRangeForPreset('this_period', payrollSettings);
             handleGenerateReport({
                 employeeIds: [initialFilterEmployeeId],
                 startDate: start,
                 endDate: end
             });
             return;
        }
    }, [employees, payrollSettings, currentCriteria, handleGenerateReport, initialFilterEmployeeId, isEmployeeView]);
    const handleExportCsv = useCallback(() => {
        if (reportsData && reportsData.length > 0) {
            const flattenedData: any[] = [];
            reportsData.forEach(report => {
                report.weeks.forEach(week => {
                    week.days.forEach(day => {
                        day.segments.forEach(segment => {
                            flattenedData.push({
                                'Employee Number': report.employeeNumber,
                                'Employee Name': report.employeeName,
                                'Day': segment.day,
                                'Date': segment.date,
                                'Start Time': segment.startTime,
                                'Stop Time': segment.stopTime,
                                'Classification': segment.activity,
                                'Unpaid Hours': segment.unpaid > 0 ? segment.unpaid.toFixed(2) : '',
                                'Regular Hours': segment.regular > 0 ? segment.regular.toFixed(2) : '',
                                'OT Hours': segment.ot > 0 ? segment.ot.toFixed(2) : '',
                            });
                        });
                    });
                });
            });
            if (flattenedData.length > 0) {
                exportToCsv(flattenedData, `detail_report_${reportsData[0].dateRange.replace(/\//g, '-').replace(/ /g, '')}.csv`);
            } else {
                 alert('No data to export.');
            }
        } else {
            alert('No data to export.');
        }
    }, [reportsData]);
	const handlePrint = () => {
        window.print();
    };
    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg">
                <p className="text-gray-600 dark:text-gray-300 flex-grow">
                    {isEmployeeView
                        ? 'View your detailed timecard report for any date range.'
                        : 'Generate detailed timecard reports for one or more employees.'}
                </p>
				{!isEmployeeView && (
                  <>
                    <Button variant="secondary" onClick={handleExportCsv} disabled={reportsData.length === 0}>
                        <DownloadIcon /> Export to CSV
                    </Button>
                    <Button variant="secondary" onClick={handlePrint}>
                        <PrintIcon /> Print Report
                    </Button>
                  </>
				)}
                <Button onClick={() => setIsModalOpen(true)}>
                    {isEmployeeView
                        ? 'Change Date Range'
                        : (currentCriteria ? 'Change Employees & Date Range' : 'Select Employees & Date Range')}
                </Button>
            </div>
            <ReportCriteriaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onGenerate={handleGenerateReport}
                employees={employeesInAnyGroup}
                title="Generate Detail Report"
                initialCriteria={currentCriteria}
                isEmployeeView={isEmployeeView}
            />
            <div>
                {reportsData.length > 0 ? (
                    <TimecardDetailReport reports={reportsData} />
                ) : (
                   currentCriteria 
                   ? <div className="text-center p-8 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow-lg">No employees had time entries during the selected period.</div>
                   : <div className="text-center p-8 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow-lg">Please select employees and a date range to generate a report.</div>
                )}
            </div>
        </div>
    );
};

export default DetailReportGenerator;